
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

export const ProtectedRoute = ({ children, requireAdmin = false }: ProtectedRouteProps) => {
  const { user, isLoading, isAdmin, forceRefresh } = useAuth();
  const location = useLocation();
  const [localLoading, setLocalLoading] = useState(true);

  // Force a refresh of auth state and manage local loading state
  useEffect(() => {
    let mounted = true;
    
    console.log('ProtectedRoute mounting with loading:', isLoading);
    
    // Set a timeout to force-exit loading state if it takes too long
    const timer = setTimeout(() => {
      if (mounted) {
        console.log('ProtectedRoute forcing exit from loading state after timeout');
        setLocalLoading(false);
      }
    }, 3000); // 3 second safety timeout
    
    // Refresh auth state when component mounts
    const refreshAuth = async () => {
      try {
        await forceRefresh();
      } catch (error) {
        console.error('Error refreshing auth in ProtectedRoute:', error);
      } finally {
        if (mounted) {
          setLocalLoading(false);
        }
      }
    };
    
    refreshAuth();
    
    return () => {
      mounted = false;
      clearTimeout(timer);
      console.log('ProtectedRoute unmounted');
    };
  }, [location.pathname]);

  // Debug current state
  useEffect(() => {
    console.log('ProtectedRoute state update:', { 
      isLoading, 
      localLoading, 
      user: user?.id, 
      isAdmin,
      path: location.pathname
    });
  }, [isLoading, localLoading, user, isAdmin, location.pathname]);

  // Use the shorter of the two loading states
  const effectiveLoading = isLoading && localLoading;

  if (effectiveLoading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
        <span className="ml-2 text-primary font-medium mt-2">Loading...</span>
      </div>
    );
  }

  if (!user) {
    console.log('ProtectedRoute: No user, redirecting to auth');
    // Redirect to login page with the return url
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (requireAdmin && !isAdmin) {
    console.log('ProtectedRoute: User not admin, redirecting to dashboard');
    // Redirect to dashboard if admin access is required but user is not an admin
    return <Navigate to="/dashboard" replace />;
  }

  console.log('ProtectedRoute: Rendering children');
  return <>{children}</>;
};
